import { motion } from 'framer-motion'
import {
  Shield,
  Cloud,
  Monitor,
  Server,
  Wrench,
  Map,
} from 'lucide-react'
import { PageMeta } from '../components/seo/PageMeta'
import { Button } from '../components/ui/Button'
import { pageMeta } from '../lib/seo'

const managedITSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Managed IT Services',
  provider: { '@type': 'Organization', name: 'NextTech Consultants' },
  description: 'Full-service outsourced IT department for Pacific Northwest SMBs.',
  areaServed: 'Pacific Northwest',
  serviceType: 'Managed IT Services',
}

const features = [
  { icon: Shield, title: '24/7 Monitoring & Proactive Resolution', body: 'We catch issues before they impact your team.' },
  { icon: Cloud, title: 'Cloud Migration & Email Setup', body: 'Seamless transitions to modern cloud infrastructure.' },
  { icon: Monitor, title: 'Device Management', body: 'Mac, PC, and mobile — all under one roof.' },
  { icon: Server, title: 'Anti-virus, Backups & Patching', body: 'Security that runs in the background.' },
  { icon: Wrench, title: 'Hardware Procurement at Cost', body: 'No markup on equipment — we find the best price.' },
  { icon: Map, title: 'Strategic Technology Roadmapping', body: 'Plan ahead so you never get caught behind.' },
  { icon: Shield, title: 'Three SPECTRA Certificates of Resilience', body: 'Endpoint Security, BEC Protection, and Backup — included for teams of 12+.' },
]

export default function ManagedIT() {
  return (
    <>
      <PageMeta
        title={pageMeta.managedIT.title}
        description={pageMeta.managedIT.description}
        canonical={pageMeta.managedIT.canonical}
        schema={managedITSchema}
      />
      <main className="min-h-screen pt-24 pb-16 px-6">
        <section className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase text-white mb-6">
            Your entire IT department. One flat monthly rate.
          </h1>
          <p className="font-body text-cream-dim text-lg">
            We handle everything your business needs to run, protect, and grow — managed IT that never sleeps.
          </p>
        </section>

        <section className="max-w-6xl mx-auto mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-lg"
                style={{ backgroundColor: 'var(--bg-3)' }}
              >
                <f.icon className="w-10 h-10 mb-4" style={{ color: 'var(--accent)' }} />
                <h3 className="font-display font-bold text-lg uppercase text-white mb-2">{f.title}</h3>
                <p className="font-body text-cream-dim">{f.body}</p>
              </motion.div>
            ))}
          </div>

          <div
            style={{
              marginTop: 48,
              padding: '36px 40px',
              background: 'var(--bg-3)',
              border: '1px solid rgba(78,125,233,0.18)',
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 32,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: 'linear-gradient(90deg, var(--accent), transparent)',
              }}
            />
            <div>
              <h3
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 900,
                  fontSize: 28,
                  textTransform: 'uppercase',
                  color: 'var(--white)',
                  marginBottom: 14,
                  lineHeight: 1.05,
                }}
              >
                {'Your security doesn\'t just run. '}
                <span style={{ color: 'var(--accent)' }}>It's certified three times over.</span>
              </h3>
              <p style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--white-dim)' }}>
                For clients with 12 or more employees, NextTech Consultants provides three SPECTRA
                Certificates of Resilience — covering Endpoint Security, BEC Protection, and Backup.
                Each is independently assessed and certified by SPECTRA. Each carries a $10,000
                warranty — $30,000 in total coverage. If the certified service fails during a covered
                event, SPECTRA refunds you directly. Included in your plan at no additional cost.
              </p>
            </div>
            <picture>
              <source srcSet="/badges/spectra-endpoint.webp" type="image/webp" />
              <img
                src="/badges/spectra-endpoint.png"
                alt="SPECTRA Certified Endpoint Security"
                width={80}
                height={80}
                style={{ height: 80, width: 80, flexShrink: 0, display: 'block' }}
              />
            </picture>
          </div>
        </section>

        <section className="max-w-4xl mx-auto text-center mb-20">
          <div className="flex flex-wrap justify-center gap-6 text-cream-dim font-body">
            <span>100+ clients</span>
            <span style={{ color: 'var(--accent)' }}>·</span>
            <span>11 years</span>
            <span style={{ color: 'var(--accent)' }}>·</span>
            <span>97%+ retention</span>
            <span style={{ color: 'var(--accent)' }}>·</span>
            <span>99.9% uptime SLA</span>
          </div>
        </section>

        <section className="max-w-4xl mx-auto text-center">
          <Button to="/contact" variant="primary">
            Book Your Free Discovery Call
          </Button>
        </section>
      </main>
    </>
  )
}
